import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export function useUnreadMessagesCount() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['hdr-unread-messages', user?.id],
    queryFn: async () => {
      if (!user) return 0;
      // Conversations the user is part of
      const { data: convos } = await supabase
        .from('conversations')
        .select('id')
        .or(`participant_one.eq.${user.id},participant_two.eq.${user.id}`);
      const ids = (convos || []).map((c: any) => c.id);
      if (!ids.length) return 0;
      const { count } = await supabase
        .from('messages')
        .select('id', { count: 'exact', head: true })
        .in('conversation_id', ids)
        .neq('sender_id', user.id)
        .eq('is_read', false);
      return count ?? 0;
    },
    enabled: !!user,
    refetchInterval: 60000,
    retry: false,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`hdr-msgs-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
        qc.invalidateQueries({ queryKey: ['hdr-unread-messages', user.id] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id, qc]);

  return data ?? 0;
}
